import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  BellIcon,
  BellSlashIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAlerts, useCreateAlert, useDeleteAlert } from '../../hooks/useAlerts';
import { useAssets } from '../../hooks/useAssets';
import { hideCurrency, formatDate } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';

const schema = z.object({
  assetId: z.string().uuid('Selecione um ativo'),
  alertType: z.enum(['PRICE_LOW', 'PRICE_HIGH']),
  thresholdValue: z.coerce.number().positive('Informe um valor'),
  message: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const ALERT_LABELS = {
  PRICE_LOW: 'Preço abaixo de',
  PRICE_HIGH: 'Preço acima de',
  DIVIDEND_COMING: 'Dividendo próximo',
};

const ALERT_COLORS = {
  PRICE_LOW: 'text-danger-400',
  PRICE_HIGH: 'text-success-400',
  DIVIDEND_COMING: 'text-primary-400',
};

export default function AlertsPage() {
  const [showForm, setShowForm] = useState(false);
  const { data: alerts, isLoading } = useAlerts();
  const { data: assets } = useAssets();
  const createAlert = useCreateAlert();
  const deleteAlert = useDeleteAlert();
  const { isVisible } = useValuesVisibility();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { alertType: 'PRICE_LOW' },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await createAlert.mutateAsync(data);
      toast.success('Alerta criado! Você receberá um e-mail quando disparar.');
      reset();
      setShowForm(false);
    } catch {
      toast.error('Erro ao criar alerta');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAlert.mutateAsync(id);
      toast.success('Alerta removido');
    } catch {
      toast.error('Erro ao remover alerta');
    }
  };

  const activeAlerts = alerts?.filter((a) => a.isActive) ?? [];
  const triggeredAlerts = alerts?.filter((a) => !a.isActive) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="page-title">Alertas de Preço</h1>
          <p className="page-subtitle">Receba e-mails automáticos quando um ativo atingir seu preço alvo</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn-primary flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          Novo alerta
        </button>
      </div>

      {/* Formulário de criação */}
      {showForm && (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-4">Configurar alerta</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Ativo</label>
                <select {...register('assetId')} className="input">
                  <option value="">Selecione...</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
                {errors.assetId && <p className="form-error">{errors.assetId.message}</p>}
              </div>

              <div>
                <label className="form-label">Tipo de alerta</label>
                <select {...register('alertType')} className="input">
                  <option value="PRICE_LOW">📉 Preço abaixo de</option>
                  <option value="PRICE_HIGH">📈 Preço acima de</option>
                </select>
              </div>

              <div>
                <label className="form-label">Preço limite (R$)</label>
                <input
                  {...register('thresholdValue')}
                  type="number"
                  step="0.01"
                  placeholder="Ex: 35.00"
                  className="input"
                />
                {errors.thresholdValue && <p className="form-error">{errors.thresholdValue.message}</p>}
              </div>

              <div>
                <label className="form-label">Mensagem (opcional)</label>
                <input
                  {...register('message')}
                  type="text"
                  placeholder="Ex: Bom ponto de entrada"
                  className="input"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">
                Cancelar
              </button>
              <button type="submit" disabled={createAlert.isPending} className="btn-primary">
                {createAlert.isPending ? 'Salvando...' : 'Criar alerta'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Alertas ativos */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <BellIcon className="h-5 w-5 text-primary-400" />
          <h2 className="text-sm font-semibold text-white">
            Ativos ({activeAlerts.length})
          </h2>
        </div>

        {isLoading ? (
          <p className="text-white/40 text-sm text-center py-8">Carregando...</p>
        ) : activeAlerts.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-white/30">
            <BellSlashIcon className="h-10 w-10 mb-3" />
            <p className="text-sm">Nenhum alerta ativo</p>
            <p className="text-xs mt-1">Crie um alerta para ser notificado por e-mail</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeAlerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white/3 border border-white/5"
              >
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-success-500 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-bold text-white">
                        {alert.assets?.code}
                      </span>
                      <span className={`text-xs font-medium ${ALERT_COLORS[alert.alertType]}`}>
                        {ALERT_LABELS[alert.alertType]}
                      </span>
                      <span className="text-sm font-semibold text-white">
                        {hideCurrency(alert.thresholdValue ?? 0, isVisible)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-white/40">
                        Atual: {hideCurrency(alert.assets?.currentPrice ?? 0, isVisible)}
                      </span>
                      {alert.message && (
                        <span className="text-xs text-white/30 italic">"{alert.message}"</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(alert.id)}
                  className="btn-ghost p-1.5 text-danger-400 hover:text-danger-300"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alertas disparados */}
      {triggeredAlerts.length > 0 && (
        <div className="card opacity-70">
          <div className="flex items-center gap-2 mb-4">
            <XCircleIcon className="h-5 w-5 text-white/30" />
            <h2 className="text-sm font-semibold text-white/50">
              Histórico de disparos ({triggeredAlerts.length})
            </h2>
          </div>
          <div className="space-y-2">
            {triggeredAlerts.slice(0, 10).map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white/2 border border-white/4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-full bg-white/5 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-medium text-white/50">
                        {alert.assets?.code}
                      </span>
                      <span className="text-xs text-white/30">
                        {ALERT_LABELS[alert.alertType]} {hideCurrency(alert.thresholdValue ?? 0, isVisible)}
                      </span>
                    </div>
                    {alert.triggeredAt && (
                      <span className="text-xs text-white/25">
                        Disparado em {formatDate(alert.triggeredAt)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(alert.id)}
                  className="btn-ghost p-1.5 text-white/20 hover:text-danger-400"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
